import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, User, Phone, Mail, GraduationCap, MapPin, Headset, MonitorPlay, Users, Briefcase, Cog, ShieldCheck, Timer, ArrowRight, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { getAttribution, getVisitorId, trackEvent } from "@/lib/analytics";
import { loadLeadFormConfigs, leadField } from "@/lib/leadForms";
import ConsentCheckbox, { CONSENT_TEXT, validateConsent } from "@/components/ConsentCheckbox";
import PhoneInput from "@/components/PhoneInput";
import { toast } from "sonner";

const STUDENT_IMG = "https://static.prod-images.emergentagent.com/jobs/fce244d9-4f06-4792-baea-c2f10101634a/images/0bb32e275218e793b908e904afaa71e1b80a1890c8f3d0e57dc42d34c061f1f5.jpeg";
const SEEN_KEY = "tb_popup_inquiry_seen";
const SEEN_AT_KEY = "tb_popup_inquiry_seen_at";

const DEFAULTS = {
  trigger_seconds: 8,
  frequency: "session",
  frequency_days: 3,
  close_on_overlay: true,
  mode_options: ["Online (Live Instructor-led)", "Classroom (Noida)", "Hybrid"],
  excluded_paths: ["/thank-you", "/login", "/signup", "/reset-password", "/verify-email", "/apply", "/pay", "/documents", "/profile", "/dashboard", "/testing"],
};

const USP = [
  { icon: MonitorPlay, title: "Live Classes", sub: "Instructor-led Training" },
  { icon: Users, title: "Industry Experts", sub: "Learn from Professionals" },
  { icon: Briefcase, title: "Placement Support", sub: "Get Career Ready" },
  { icon: Cog, title: "Hands-on Projects", sub: "Build Real Skills" },
];

const PARTNERS = ["Deloitte", "Capgemini", "Accenture", "HCLTech", "Wipro"];

const INPUT_CLS = "w-full h-12 rounded-xl border border-slate-300 bg-white pl-10 pr-4 text-sm font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FFC72C] focus:border-[#FFC72C] transition-shadow";

function PartnerStrip({ dark }) {
  return (
    <div data-testid="popup-inquiry-partners">
      <div className={`flex items-center gap-3 ${dark ? "text-white/50" : "text-slate-400"}`}>
        <span className={`h-px flex-1 ${dark ? "bg-white/15" : "bg-slate-200"}`} />
        <span className="text-[10px] font-bold uppercase tracking-widest">Our Students Work At</span>
        <span className={`h-px flex-1 ${dark ? "bg-white/15" : "bg-slate-200"}`} />
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
        {PARTNERS.map((n) => (
          <span key={n} className="rounded-lg bg-white px-3 py-1.5 text-[11px] font-extrabold text-slate-800 shadow-sm">{n}</span>
        ))}
      </div>
    </div>
  );
}

export default function PopupInquiryModal() {
  const location = useLocation();
  const [cfg, setCfg] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", program_slug: "", training_mode: "" });
  const [consent, setConsent] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => { loadLeadFormConfigs().then((f) => setCfg(f.popup_inquiry || null)); }, []);
  useEffect(() => { api.get("/programs").then((r) => setPrograms(r.data.programs || [])).catch(() => {}); }, []);

  const p = { ...DEFAULTS, ...((cfg && cfg.popup) || {}) };

  useEffect(() => {
    if (!cfg || cfg.active === false) return undefined;
    const path = location.pathname;
    if ((p.excluded_paths || []).some((x) => path === x || path.startsWith(`${x}/`))) return undefined;
    if (p.frequency === "session" && sessionStorage.getItem(SEEN_KEY)) return undefined;
    if (p.frequency === "days") {
      const last = Number(localStorage.getItem(SEEN_AT_KEY) || 0);
      if (last && Date.now() - last < p.frequency_days * 86400000) return undefined;
    }
    const show = () => {
      setOpen(true);
      sessionStorage.setItem(SEEN_KEY, "1");
      localStorage.setItem(SEEN_AT_KEY, String(Date.now()));
      trackEvent("form_view", { form: "popup_inquiry" });
    };
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (p.trigger === "exit_intent" && !isTouch) {
      const onLeave = (e) => {
        if (!e.relatedTarget && e.clientY <= 0) {
          document.removeEventListener("mouseout", onLeave);
          show();
        }
      };
      timerRef.current = setTimeout(() => document.addEventListener("mouseout", onLeave), 3000);
      return () => { clearTimeout(timerRef.current); document.removeEventListener("mouseout", onLeave); };
    }
    timerRef.current = setTimeout(show, Math.max(2, p.trigger_seconds || 8) * 1000);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, location.pathname]);

  if (!cfg || cfg.active === false) return null;

  const fName = leadField(cfg, "name", { label: "Full Name", required: true });
  const fPhone = leadField(cfg, "phone", { label: "Phone Number", required: true });
  const fEmail = leadField(cfg, "email", { label: "Email Address", required: true });
  const fProg = leadField(cfg, "program_slug", { label: "Select a Program / Course", required: true });
  const fMode = leadField(cfg, "training_mode", { label: "Select Preferred Training Mode / Branch" });

  const submit = async (e) => {
    e.preventDefault();
    if (!validateConsent(consent)) return;
    setLoading(true);
    try {
      const { data } = await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        program_slug: form.program_slug,
        training_mode: form.training_mode,
        source: "popup_inquiry",
        attribution: getAttribution(),
        visitor_id: getVisitorId(),
        page: window.location.pathname,
        consent_given: consent,
        consent_text: CONSENT_TEXT,
      });
      trackEvent("inquiry_submit", { form: "popup_inquiry", program_slug: form.program_slug });
      setDone(true);
      toast.success(data.message || "Inquiry received");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setDone(false); }}>
      <DialogContent
        className="p-0 border-0 overflow-hidden rounded-3xl w-[calc(100vw-2rem)] max-w-md md:max-w-4xl max-h-[92vh] overflow-y-auto [&>button]:hidden"
        onInteractOutside={(e) => { if (!p.close_on_overlay) e.preventDefault(); }}
        data-testid="popup-inquiry-modal"
      >
        <DialogTitle className="sr-only">{cfg.title || "Kick Start Your IT Career"}</DialogTitle>
        <DialogDescription className="sr-only">{cfg.subtitle || "Book a free counselling session today"}</DialogDescription>
        <div className="grid md:grid-cols-[1.05fr_1fr]">
          {/* Branded panel */}
          <div className="relative bg-[#0B0B0F] text-white px-6 pt-6 pb-6 md:p-8 overflow-hidden">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#FFC72C]/20 blur-3xl pointer-events-none" />
            <div className="absolute -left-10 bottom-24 w-40 h-40 rounded-full bg-[#FFC72C]/10 blur-2xl pointer-events-none" />
            <img src={STUDENT_IMG} alt="" loading="lazy"
              className="absolute bottom-0 right-0 w-28 sm:w-36 md:w-52 object-contain pointer-events-none select-none [mask-image:linear-gradient(to_top,black_80%,transparent)]" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#FFC72C] text-black text-[11px] font-extrabold px-3.5 py-1.5">
                <GraduationCap className="w-3.5 h-3.5" /> Admissions Open
                <Sparkles className="w-3.5 h-3.5" />
              </span>
              <h3 className="mt-4 text-2xl sm:text-3xl md:text-4xl font-extrabold leading-[1.15] tracking-tight max-w-[85%] md:max-w-[70%]">
                Master In-Demand <span className="text-[#FFC72C]">IT Skills</span> for a Brighter Future!
              </h3>
              <p className="mt-3 text-xs sm:text-sm text-white/70 max-w-[80%] md:max-w-[60%] leading-relaxed">
                Get expert guidance, live training and real-world projects to become job ready.
              </p>
              <div className="grid grid-cols-4 gap-2 md:gap-3 mt-16 sm:mt-20 md:mt-24">
                {USP.map((u) => (
                  <div key={u.title} className="rounded-xl bg-white/5 border border-white/10 px-2 py-3 text-center backdrop-blur-sm">
                    <u.icon className="w-5 h-5 text-[#FFC72C] mx-auto" />
                    <p className="text-[10px] sm:text-[11px] font-extrabold mt-1.5 leading-tight">{u.title}</p>
                    <p className="hidden sm:block text-[9px] text-white/50 mt-0.5 leading-tight">{u.sub}</p>
                  </div>
                ))}
              </div>
              <div className="hidden md:block mt-6"><PartnerStrip dark /></div>
            </div>
          </div>

          {/* Form panel */}
          <div className="relative bg-white px-6 py-6 md:p-8">
            <button onClick={() => setOpen(false)} aria-label="Close pop-up"
              className="absolute top-4 right-4 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 p-2 transition-colors"
              data-testid="popup-inquiry-close">
              <X className="w-4 h-4" />
            </button>
            {done ? (
              <div className="py-14 text-center" data-testid="popup-inquiry-success">
                <span className="w-16 h-16 rounded-full bg-[#FFC72C]/15 text-[#9A7B0A] flex items-center justify-center mx-auto">
                  <ShieldCheck className="w-8 h-8" />
                </span>
                <p className="mt-5 text-xl font-extrabold text-slate-900">{cfg.success_title || "You're all set!"}</p>
                <p className="mt-2 text-sm text-slate-500 max-w-xs mx-auto">{cfg.success_message || "Our counsellor will call you within 24 hours."}</p>
                <button onClick={() => setOpen(false)}
                  className="mt-7 rounded-xl bg-[#FFC72C] hover:bg-[#F5B800] text-black font-extrabold px-8 h-11 text-sm transition-colors"
                  data-testid="popup-inquiry-done-close">Done</button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 pr-10">
                  <span className="w-11 h-11 rounded-xl bg-[#FFC72C] text-black flex items-center justify-center shrink-0">
                    <Headset className="w-5 h-5" />
                  </span>
                  <div>
                    <h4 className="text-lg font-extrabold text-slate-900 leading-tight">{cfg.title || "Kick Start Your IT Career"}</h4>
                    <p className="text-xs text-slate-500 mt-0.5">{cfg.subtitle || "Book a free counselling session today"}</p>
                  </div>
                </div>
                <form onSubmit={submit} className="flex flex-col gap-3 mt-5">
                  {fName.visible && (
                    <div className="relative">
                      <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input required={!!fName.required} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder={`${fName.label}${fName.required ? " *" : ""}`} className={INPUT_CLS} data-testid="popup-inquiry-name-input" />
                    </div>
                  )}
                  {fPhone.visible && (
                    <div className="relative">
                      <PhoneInput required={!!fPhone.required} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })}
                        placeholder={fPhone.label} inputCls={INPUT_CLS.replace("pl-10", "pl-4")} testid="popup-inquiry-phone-input" />
                    </div>
                  )}
                  {fEmail.visible && (
                    <div className="relative">
                      <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input type="email" required={!!fEmail.required} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder={`${fEmail.label}${fEmail.required ? " *" : ""}`} className={INPUT_CLS} data-testid="popup-inquiry-email-input" />
                    </div>
                  )}
                  {fProg.visible && (
                    <div className="relative">
                      <GraduationCap className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <select required={!!fProg.required} value={form.program_slug} onChange={(e) => setForm({ ...form, program_slug: e.target.value })}
                        className={`${INPUT_CLS} appearance-none ${form.program_slug ? "" : "text-slate-400"}`} data-testid="popup-inquiry-program-select">
                        <option value="">{fProg.label}{fProg.required ? " *" : ""}</option>
                        {programs.map((pr) => <option key={pr.slug} value={pr.slug}>{pr.title}</option>)}
                      </select>
                      <span className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 text-xs">▾</span>
                    </div>
                  )}
                  {fMode.visible && (
                    <div className="relative">
                      <MapPin className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <select required={!!fMode.required} value={form.training_mode} onChange={(e) => setForm({ ...form, training_mode: e.target.value })}
                        className={`${INPUT_CLS} appearance-none ${form.training_mode ? "" : "text-slate-400"}`} data-testid="popup-inquiry-mode-select">
                        <option value="">{fMode.label}{fMode.required ? " *" : ""}</option>
                        {(p.mode_options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <span className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 text-xs">▾</span>
                    </div>
                  )}
                  <ConsentCheckbox checked={consent} onChange={setConsent} testid="popup-inquiry-consent" />
                  <button type="submit" disabled={loading}
                    className="w-full h-12 rounded-xl bg-[#FFC72C] hover:bg-[#F5B800] disabled:opacity-60 text-black font-extrabold text-sm flex items-center justify-center gap-2 transition-colors"
                    data-testid="popup-inquiry-submit-button">
                    {loading ? "Sending…" : (cfg.button_label || "Get Free Counselling")} {!loading && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>
                <div className="md:hidden mt-5"><PartnerStrip /></div>
                <div className="flex items-center justify-between gap-2 mt-5 pt-4 border-t border-slate-100" data-testid="popup-inquiry-trust">
                  {[{ icon: ShieldCheck, t: "100% Free", s: "Career Guidance" }, { icon: Users, t: "Expert", s: "Counsellors" }, { icon: Timer, t: "Quick", s: "Response" }].map((b) => (
                    <div key={b.t + b.s} className="flex items-center gap-2">
                      <b.icon className="w-4 h-4 text-[#FFC72C] shrink-0" />
                      <span className="text-[10px] leading-tight text-slate-600"><span className="font-extrabold text-slate-800 block">{b.t}</span>{b.s}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
